import { useSelector } from "react-redux";
import "../submitToBilling.scss";
import "../billing-collapse.scss";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { debounce } from "lodash";
import { selectUserId } from "../../../../redux/slices/userSlice";
import { clientBilling } from "../tableColumns";
import K from "../../../../utilities/constants";
import BillingApproval from "../../../../redux/models/billingApproval";
import { ServerSideDataGrid } from "../../../../common/components/serverSideDataGrid/serverSideDataGrid";
import { PaginationSizeSelector } from "../../../../common/tableComponents/pagination/paginationSizeSelector";
import {
  calculateTableHeight,
  onFilterChanged,
  differentCurrencyWarning,
  changeTableHeight,
} from "./billingComponentsHelper";
import AgGridUtility from "../../../../utilities/agGridUtility";

export const ClientBillingsTable = ({
  clientId,
  clientName,
  pageStates,
  setPageStates,
  setTotalLoading,
  onBillingSelected,
}) => {
  const userId = useSelector(selectUserId);
  const gridRef = useRef(null);

  const [height, setHeight] = useState(322);
  const [availableIds, setAvailableIds] = useState([]);
  const [selectedIds, setSelectedIds] = useState([]);

  const tableColumns = useMemo(() => clientBilling, []);

  useEffect(() => {
    BillingApproval.getClientSubmittedBillingIds(clientId).then((r) =>
      setAvailableIds(r.data),
    );
  }, []);

  const getClientServerSideData = useCallback(
    (options) => {
      return BillingApproval.getClientsSubmittedBillings(clientId, {
        ...options,
      });
    },
    [clientId],
  );

  useEffect(() => {
    if (pageStates.clientLoading) {
      gridRef?.current?.api?.deselectAll();
      gridRef?.current?.api?.refreshServerSide();
    }
    setPageStates((prev) => ({ ...prev, clientLoading: false }));
  }, [pageStates.clientLoading]);

  const onDataFetchFailed = useCallback((err) => console.error(err), []);
  const onDataFetchSuccess = useCallback(
    (data) => setHeight(calculateTableHeight(data.rowData)),
    [],
  );

  const onRowSelected = () => {
    setTotalLoading((prev) => ({ ...prev, client: true }));
  };

  const onSelectionChanged = debounce(async (event) => {
    await updateSelection(event.api);
  }, 1000);

  const updateSelection = async (gridApi) => {
    const selectedRows = gridApi.getServerSideSelectionState();
    const updatedSelectedRows = AgGridUtility.getFilteredSelectedRows(
      gridApi,
      availableIds,
    );
    setSelectedIds(updatedSelectedRows);
    const filterModel = gridApi.getFilterModel();
    const isFilterApplied = filterModel?.isManual?.values.length > 0;

    const res = await BillingApproval.getClientBillingsTotal(clientId, {
      selectAll: isFilterApplied ? false : selectedRows.selectAll,
      toggledNodes: isFilterApplied
        ? updatedSelectedRows.map(String)
        : selectedRows.toggledNodes,
    });
    setTotalLoading((prev) => ({ ...prev, client: false }));
    const { total, isSameType, totalCount } = res.data;

    setPageStates((prev) => ({
      ...prev,
      checkedRows: {
        ...pageStates.checkedRows,
        clientTotal: total,
        clientSelectAll: isFilterApplied ? false : selectedRows.selectAll,
        clientSelectedNodes: isFilterApplied
          ? updatedSelectedRows.map(String)
          : selectedRows.toggledNodes,
        isSameTypeClients: totalCount === 0 || !isSameType,
      },
    }));

    if (!isSameType) differentCurrencyWarning();
  };

  return (
    <ServerSideDataGrid
      minHeight={322}
      height={height}
      ref={gridRef}
      userId={userId}
      configKey={K.AgGridTable.Keys.SubmittedClientBillingsVisibleColumn}
      getData={getClientServerSideData}
      defaultColumnConfig={tableColumns}
      defaultColDef={K.AgGridTable.DefaultColDef}
      onDataFetchFailed={onDataFetchFailed}
      pagination
      paginationPageSize={25}
      cacheBlockSize={25}
      getContextMenuItems={() => AgGridUtility.menuItemsWithExport}
      rowSelection="multiple"
      suppressRowClickSelection
      onFilterChanged={(event) =>
        onFilterChanged(event.api, setHeight, selectedIds, updateSelection)
      }
      onPaginationChanged={(event) => changeTableHeight(event.api, setHeight)}
      rowStyle={{ cursor: "pointer" }}
      onRowClicked={(event) => {
        onBillingSelected({
          type: K.BillingType.Client,
          details: event.data,
          gridApi: gridRef.current?.api,
          clientName,
        });
      }}
      onDataFetchSuccess={onDataFetchSuccess}
      onSelectionChanged={onSelectionChanged}
      onRowSelected={onRowSelected}
      components={{
        PaginationSizeSelector: (props) => (
          <PaginationSizeSelector
            {...props}
            gridRef={gridRef}
            getData={getClientServerSideData}
          />
        ),
      }}
      statusBar={{
        statusPanels: [
          {
            statusPanel: "PaginationSizeSelector",
            align: "left",
          },
        ],
      }}
    />
  );
};
